import React, { useState, useRef, useCallback } from 'react';
import { Mic, MicOff, Square, Play, Pause, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface AudioRecorderProps {
  onAudioReady: (audioFile: File) => void;
  onCancel?: () => void;
  disabled?: boolean;
  className?: string;
}

export const AudioRecorder: React.FC<AudioRecorderProps> = ({
  onAudioReady,
  onCancel,
  disabled = false,
  className
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasRecording, setHasRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioBlobRef = useRef<Blob | null>(null);

  const startTimer = useCallback(() => {
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Get supported MIME types in order of preference
  const getSupportedMimeType = useCallback(() => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
      'audio/ogg'
    ];
    
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        console.log(`✅ Using MIME type: ${type}`);
        return type;
      }
    }
    
    console.warn('⚠️ No preferred MIME types supported, using default');
    return '';
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      console.log('🎤 Starting audio recording...');

      // Request microphone access with specific constraints
      const constraints = {
        audio: {
          channelCount: 1, // Mono audio
          sampleRate: 44100, // Standard sample rate
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: false
      };

      console.log('📋 Audio constraints:', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Log stream info
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length > 0) {
        const track = audioTracks[0];
        console.log('🎵 Audio track settings:', track.getSettings());
        console.log('🎵 Audio track constraints:', track.getConstraints());
      }

      // Get supported MIME type
      const mimeType = getSupportedMimeType();
      
      // Create MediaRecorder with options
      const options: MediaRecorderOptions = {
        audioBitsPerSecond: 128000
      };
      
      if (mimeType) {
        options.mimeType = mimeType;
      }

      console.log('⚙️ MediaRecorder options:', options);
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;

      // Reset chunks
      audioChunksRef.current = [];

      // Set up event handlers
      mediaRecorder.onstart = () => {
        console.log('▶️ MediaRecorder started');
        console.log('📊 MediaRecorder state:', mediaRecorder.state);
        console.log('📊 MediaRecorder mimeType:', mediaRecorder.mimeType);
      };

      mediaRecorder.ondataavailable = (event) => {
        console.log('📦 Data available event:', {
          dataSize: event.data.size,
          type: event.data.type,
          timestamp: new Date().toISOString()
        });
        
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          console.log(`📊 Total chunks collected: ${audioChunksRef.current.length}`);
        } else {
          console.warn('⚠️ Received empty data chunk');
        }
      };

      mediaRecorder.onstop = () => {
        console.log('⏹️ MediaRecorder stopped');
        console.log(`📊 Final chunks count: ${audioChunksRef.current.length}`);
        
        if (audioChunksRef.current.length === 0) {
          console.error('❌ No audio chunks collected!');
          setError('Aucun audio enregistré. Veuillez réessayer.');
          return;
        }

        // Calculate total size
        const totalSize = audioChunksRef.current.reduce((sum, chunk) => sum + chunk.size, 0);
        console.log(`📊 Total audio data size: ${totalSize} bytes`);

        if (totalSize === 0) {
          console.error('❌ Total audio size is 0!');
          setError('Audio vide enregistré. Veuillez réessayer.');
          return;
        }

        // Create blob with proper MIME type
        const finalMimeType = mediaRecorder.mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: finalMimeType });
        audioBlobRef.current = audioBlob;

        console.log('🎵 Audio blob created:', {
          size: audioBlob.size,
          type: audioBlob.type
        });

        // Create URL for playback
        const audioUrl = URL.createObjectURL(audioBlob);
        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          audioRef.current.load();
          
          audioRef.current.onloadedmetadata = () => {
            console.log('ℹ️ Audio metadata loaded:', {
              duration: audioRef.current?.duration,
              readyState: audioRef.current?.readyState
            });
          };

          audioRef.current.oncanplay = () => {
            console.log('✅ Audio can play');
          };

          audioRef.current.onerror = (e) => {
            console.error('❌ Audio playback error:', e);
          };
        }

        setHasRecording(true);
      };

      mediaRecorder.onerror = (event) => {
        console.error('❌ MediaRecorder error:', event);
        setError('Erreur lors de l\'enregistrement audio.');
      };

      // Start recording with timeslice for regular data events
      mediaRecorder.start(1000); // 1 second timeslice
      setIsRecording(true);
      setRecordingTime(0);
      startTimer();

    } catch (err) {
      console.error('❌ Error starting recording:', err);
      setError('Impossible d\'accéder au microphone. Veuillez vérifier les permissions.');
    }
  }, [getSupportedMimeType, startTimer]);

  const stopRecording = useCallback(() => {
    console.log('🛑 Stopping recording...');
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      // Request final data before stopping
      mediaRecorderRef.current.requestData();
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log('🔇 Audio track stopped');
      });
      streamRef.current = null;
    }

    setIsRecording(false);
    stopTimer();
  }, [stopTimer]);

  const playRecording = useCallback(() => {
    if (audioRef.current && hasRecording) {
      console.log('▶️ Playing recording...');
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch(err => {
          console.error('❌ Error playing audio:', err);
          setError('Erreur lors de la lecture audio.');
        });
    }
  }, [hasRecording]);

  const pauseRecording = useCallback(() => {
    if (audioRef.current) {
      console.log('⏸️ Pausing playback...');
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  const deleteRecording = useCallback(() => {
    console.log('🗑️ Deleting recording...');
    
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    
    audioChunksRef.current = [];
    audioBlobRef.current = null;
    setHasRecording(false);
    setIsPlaying(false);
    setRecordingTime(0);
    setError(null);
  }, []);

  // Convert audio blob to WAV format
  const convertToWAV = useCallback(async (audioBlob: Blob): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const fileReader = new FileReader();
      
      fileReader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          
          console.log('🔄 Converting to WAV:', {
            sampleRate: audioBuffer.sampleRate,
            channels: audioBuffer.numberOfChannels,
            duration: audioBuffer.duration
          });
          
          // Convert to WAV
          const wavBuffer = audioBufferToWav(audioBuffer);
          const wavBlob = new Blob([wavBuffer], { type: 'audio/wav' });
          
          console.log('✅ WAV conversion completed:', {
            originalSize: audioBlob.size,
            wavSize: wavBlob.size,
            type: wavBlob.type
          });
          
          resolve(wavBlob);
        } catch (error) {
          console.error('❌ Error converting audio to WAV:', error);
          reject(error);
        }
      };
      
      fileReader.onerror = () => reject(new Error('Failed to read audio file'));
      fileReader.readAsArrayBuffer(audioBlob);
    });
  }, []);

  // Convert AudioBuffer to WAV format
  const audioBufferToWav = useCallback((buffer: AudioBuffer): ArrayBuffer => {
    const length = buffer.length;
    const numberOfChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const bytesPerSample = 2;
    const blockAlign = numberOfChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = length * blockAlign;
    const bufferSize = 44 + dataSize;
    
    const arrayBuffer = new ArrayBuffer(bufferSize);
    const view = new DataView(arrayBuffer);
    
    // WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, bufferSize - 8, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bytesPerSample * 8, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);
    
    // Convert audio data
    let offset = 44;
    for (let i = 0; i < length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]));
        view.setInt16(offset, sample * 0x7FFF, true);
        offset += 2;
      }
    }
    
    return arrayBuffer;
  }, []);

  const sendRecording = useCallback(async () => {
    if (!audioBlobRef.current) {
      console.error('❌ No audio blob to send');
      setError('Aucun enregistrement à envoyer.');
      return;
    }

    try {
      console.log('📤 Sending recording...');
      console.log('📊 Original audio blob info:', {
        size: audioBlobRef.current.size,
        type: audioBlobRef.current.type
      });

      // Convert to WAV format for server compatibility
      const wavBlob = await convertToWAV(audioBlobRef.current);

      // Create File object with WAV format
      const audioFile = new File(
        [wavBlob], 
        `recording-${Date.now()}.wav`, 
        { type: 'audio/wav' }
      );

      console.log('📁 WAV audio file created:', {
        name: audioFile.name,
        size: audioFile.size,
        type: audioFile.type,
        lastModified: audioFile.lastModified
      });

      onAudioReady(audioFile);
    } catch (error) {
      console.error('❌ Error processing audio for upload:', error);
      setError('Erreur lors de la conversion audio. Veuillez réessayer.');
    }
  }, [onAudioReady, convertToWAV]);

  // Audio element event handlers
  const handleAudioEnded = useCallback(() => {
    console.log('🏁 Audio playback ended');
    setIsPlaying(false);
  }, []);

  const handleAudioError = useCallback((e: React.SyntheticEvent<HTMLAudioElement, Event>) => {
    console.error('❌ Audio element error:', e);
    setError('Erreur lors de la lecture audio.');
    setIsPlaying(false);
  }, []);

  return (
    <div className={cn('flex flex-col space-y-4 p-4 border rounded-lg bg-background', className)}>
      {/* Hidden audio element for playback */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnded}
        onError={handleAudioError}
        preload="metadata"
      />

      {/* Recording controls */}
      <div className="flex items-center justify-center space-x-2">
        {!isRecording && !hasRecording && (
          <Button
            onClick={startRecording}
            disabled={disabled}
            variant="default"
            size="lg"
            className="flex items-center space-x-2"
          >
            <Mic className="w-5 h-5" />
            <span>Commencer l'enregistrement</span>
          </Button>
        )}

        {isRecording && (
          <Button
            onClick={stopRecording}
            variant="destructive"
            size="lg"
            className="flex items-center space-x-2"
          >
            <Square className="w-5 h-5" />
            <span>Arrêter ({formatTime(recordingTime)})</span>
          </Button>
        )}

        {hasRecording && !isRecording && (
          <div className="flex items-center space-x-2">
            <Button
              onClick={isPlaying ? pauseRecording : playRecording}
              variant="outline"
              size="sm"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>

            <Button
              onClick={deleteRecording}
              variant="outline"
              size="sm"
            >
              <Trash2 className="w-4 h-4" />
            </Button>

            <Button
              onClick={sendRecording}
              variant="default"
              size="sm"
            >
              Envoyer
            </Button>

            {onCancel && (
              <Button
                onClick={onCancel}
                variant="ghost"
                size="sm"
              >
                Annuler
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Recording status */}
      {isRecording && (
        <div className="flex items-center justify-center space-x-2 text-red-600">
          <MicOff className="w-4 h-4 animate-pulse" />
          <span>Enregistrement en cours... {formatTime(recordingTime)}</span>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded">
          {error}
        </div>
      )}

      {/* Recording info */}
      {hasRecording && !isRecording && (
        <div className="text-sm text-gray-600 text-center">
          Enregistrement prêt ({formatTime(recordingTime)})
        </div>
      )}
    </div>
  );
};